import { FC, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Col,
  Form,
  Row,
  CircleCore,
  InputCore,
  SelectCore,
  TextAreaCore,
  ButtonCore,
  CharacterCard,
  Tooltip,
  StyleCard,
} from '@components';
import { useCreateStory } from '@coreStory/useCase';
import { AuthModal } from '@app/auth';
import Loading from '@app/statics/Loading';
import {
  ageOptions,
  languagesOptions,
  perspectiveOptions,
  styleOptions,
  toneOptions,
  traningTopicsOptions,
} from './constants';
import type { ICreateStory } from '@coreStory/type';
import CreateStoryImg from '@assets/images/create-story.png';
import styles from './index.module.scss';
import { useStore } from '@store';
import { writeTheme } from 'src/helpers/theme';
import NarrativeCard from 'src/components/NarrativeCard';
import VoiceVibeCard from 'src/components/VoiceVibeCard';
import { MdOutlineChevronLeft, MdOutlineChevronRight } from 'react-icons/md';

const createTooltipLabel = (label: string, tooltip: string) => (
  <Tooltip title={tooltip} placement="top">
    <span style={{ cursor: 'help' }}>
      {label} <span style={{ color: '#1890ff', fontSize: '12px' }}>ⓘ</span>
    </span>
  </Tooltip>
);

const CreateStory: FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [selectedCharacter, setSelectedCharacter] = useState<string>('Girl');
  const [selectedNarrative, setSelectedNarrative] = useState<string>('alloy');
  const [selectedVibe, setSelectedVibe] = useState<string>('Auto');
  const [selectedStyle, setSelectedStyle] = useState<string>('Cartoon');
  const styleSwiperRef = useRef<any>(null);
  const narrativeSwiperRef = useRef<any>(null);
  const voiceVibeSwiperRef = useRef<any>(null);

  const [isAuthModalOpen, setAuthModalOpen] = useState<boolean>(false);
  const useAddStory = useCreateStory();
  const { setTheme } = useStore();
  // useEffect(() => {
  //   const hour = new Date().getHours();
  //   if (hour > 5 && hour < 17) {
  //     setTheme('day');
  //     writeTheme('day');
  //   } else {
  //     setTheme('night');
  //     writeTheme('night');
  //   }
  // }, []);
  const { isAuthenticated } = useStore();

  const handleSubmit = (value: ICreateStory) => {
    if (isAuthenticated) {
      const data = {
        ...value,
        kids_character: selectedCharacter,
        voice_speaker: selectedNarrative,
        voice_vibe: selectedVibe,
        style: selectedStyle,
      };
      useAddStory.mutate(data);
    } else {
      setAuthModalOpen(true);
    }
  };
  useEffect(() => {
    if (useAddStory.isSuccess)
      navigate(`/story/${useAddStory.data?.story_id}`, { replace: true });
  }, [useAddStory.isSuccess]);

  return (
    <>
      {useAddStory.isLoading ? (
        <Loading />
      ) : (
        <div className={styles['create_story_container']}>
          <div className={styles['create_story_header']}>
            <img
              src={CreateStoryImg}
              width={372}
              height={243}
              alt={'Create Story Image'}
            />
            <h1 className="title text-purple">Create Story</h1>
          </div>
          <Form
            form={form}
            className={styles['create_story_steps']}
            labelCol={{ span: 24 }}
            onFinish={handleSubmit}>
            <div className={styles['steps_cards']}>
              <div className={styles.card_title}>
                <CircleCore color="purple">1</CircleCore>
                <h2 className="main-title text-purple">
                  Insert Basic Information
                </h2>
              </div>
              <Row gutter={40}>
                <Col md={12} xs={24}>
                  <Form.Item
                    label="Child’s Name"
                    name="kids_name"
                    rules={[{ required: true, message: '' }]}>
                    <InputCore />
                  </Form.Item>
                </Col>
                <Col md={12} xs={24}>
                  <Form.Item
                    label="Age"
                    name="kids_age"
                    rules={[{ required: true, message: '' }]}>
                    <SelectCore options={ageOptions} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div className={styles['steps_cards']}>
              <div className={styles.card_title}>
                <CircleCore color="purple">2</CircleCore>
                <h2 className="main-title text-purple">Description</h2>
              </div>
              <Row gutter={40}>
                <Col md={12} xs={24}>
                  <Form.Item
                    label="Language"
                    name="lang"
                    rules={[{ required: true, message: '' }]}
                    initialValue={'English'}>
                    <SelectCore options={languagesOptions} />
                  </Form.Item>
                </Col>
                <Col md={12} xs={24}>
                  <Form.Item
                    label="Select training topic:"
                    name="training_topic"
                    rules={[{ required: true, message: '' }]}>
                    <SelectCore
                      options={traningTopicsOptions}
                      hasFilter={true}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={40}>
                <Col xs={24}>
                  <Form.Item
                    label="Describe your story:"
                    name="story_desc"
                    rules={[{ required: true, message: '' }]}>
                    <TextAreaCore
                      rows={6}
                      placeholder="Example : I want to teach my daughter the topic of addition in math"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
            <div className={styles['steps_cards']}>
              <div className={styles.card_title}>
                <CircleCore color="purple">3</CircleCore>
                <h2 className="main-title text-purple">
                  Set the Mood and Point of View
                </h2>
              </div>
              <Row gutter={40}>
                <Col md={12} xs={24}>
                  <Form.Item
                    label={createTooltipLabel(
                      'Select Story Point of View',
                      'Choose the perspective from which the story will be told.',
                    )}
                    name="point_of_view"
                    rules={[{ required: true, message: '' }]}>
                    <SelectCore options={perspectiveOptions} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={40}>
                <Col md={12} xs={24}>
                  <Form.Item
                    label={createTooltipLabel(
                      'Choose Story Style',
                      'Select the style that fits your story.',
                    )}
                    name="story_style"
                    rules={[{ required: true, message: '' }]}>
                    <SelectCore options={styleOptions} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={40}>
                <Col md={12} xs={24}>
                  <Form.Item
                    label={createTooltipLabel(
                      'Choose Story Tone',
                      'Select the tone that fits the emotion of your story.',
                    )}
                    name="story_tone"
                    rules={[{ required: true, message: '' }]}>
                    <SelectCore options={toneOptions} />
                  </Form.Item>
                </Col>
              </Row>
            </div>
            <div className={styles['steps_cards']}>
              <div className={styles.card_title}>
                <CircleCore color="purple">4</CircleCore>
                <h2 className="main-title text-purple">
                  Choose your story elements
                </h2>
              </div>
              <Row gutter={40}>
                <Col xs={24}>
                  <CharacterCard
                    character={selectedCharacter}
                    setCharacter={setSelectedCharacter}
                  />
                </Col>
              </Row>
              <Row gutter={40}>
                <Col xs={24}>
                  <div className={styles.sliderTitle}>
                    <h3 className="main-title text-purple">
                      Choose Style Image
                    </h3>
                    <div className={styles.slider_button_wrapper}>
                      <button
                        className={styles.slider_button}
                        type="button"
                        onClick={() => styleSwiperRef.current?.slidePrev()}>
                        <MdOutlineChevronLeft />
                      </button>
                      <button
                        className={styles.slider_button}
                        type="button"
                        onClick={() => styleSwiperRef.current?.slideNext()}>
                        <MdOutlineChevronRight />
                      </button>
                    </div>
                  </div>
                  <StyleCard
                    ref={styleSwiperRef}
                    selectedStyle={selectedStyle}
                    setSelectedStyle={setSelectedStyle}
                  />
                </Col>
              </Row>
            </div>
            <div className={styles['steps_cards']}>
              <div className={styles.card_title}>
                <CircleCore color="purple">5</CircleCore>
                <h2 className="main-title text-purple">
                  Story Narrative & Emotional Tone
                </h2>
              </div>
              <div className={styles.sliderTitle}>
                <h3 className="main-title text-purple">Story Narrative</h3>
                <div className={styles.slider_button_wrapper}>
                  <button
                    className={styles.slider_button}
                    type="button"
                    onClick={() => narrativeSwiperRef.current?.slidePrev()}>
                    <MdOutlineChevronLeft size={24} />
                  </button>
                  <button
                    className={styles.slider_button}
                    type="button"
                    onClick={() => narrativeSwiperRef.current?.slideNext()}>
                    <MdOutlineChevronRight size={24} />
                  </button>
                </div>
              </div>
              <Row gutter={40}>
                <Col xs={24}>
                  <NarrativeCard
                    ref={narrativeSwiperRef}
                    narrative={selectedNarrative}
                    setNarrative={setSelectedNarrative}
                  />
                </Col>
              </Row>
              <div className={styles.sliderTitle}>
                <h3 className="main-title text-purple">Emotional Tone</h3>
                <div className={styles.slider_button_wrapper}>
                  <button
                    className={styles.slider_button}
                    type="button"
                    onClick={() => voiceVibeSwiperRef.current?.slidePrev()}>
                    <MdOutlineChevronLeft size={24} />
                  </button>
                  <button
                    className={styles.slider_button}
                    type="button"
                    onClick={() => voiceVibeSwiperRef.current?.slideNext()}>
                    <MdOutlineChevronRight size={24} />
                  </button>
                </div>
              </div>
              <Row gutter={40}>
                <Col xs={24}>
                  <VoiceVibeCard
                    ref={voiceVibeSwiperRef}
                    selectedNarrative={selectedNarrative}
                    selectedVibe={selectedVibe}
                    setSelectedVibe={setSelectedVibe}
                  />
                </Col>
              </Row>
            </div>
            <div className={styles['button_card']}>
              <ButtonCore
                type="primary"
                htmlType={'submit'}
                loading={useAddStory.isLoading}>
                Create Story
              </ButtonCore>
            </div>
          </Form>
        </div>
      )}

      <AuthModal isOpen={isAuthModalOpen} setIsModalOpen={setAuthModalOpen} />
    </>
  );
};

export default CreateStory;
